/**
 * Asset Intelligence Module Configuration
 *
 * Deep-dive analysis of individual assets with health monitoring,
 * anomaly detection, and time-to-failure predictions.
 */

import { ModuleConfig } from './moduleTypes'

export const assetIntelligenceConfig: ModuleConfig = {
  id: 'asset-intelligence',
  name: 'Asset Intelligence',
  description: 'Deep analysis of individual asset health and anomalies',
  icon: 'Activity',
  basePath: '/assets',
  enabledIndustries: ['manufacturing', 'oil_gas', 'healthcare', 'financial', 'energy', 'transportation', 'mining', 'chemicals', 'food_beverage', 'pharmaceuticals', 'aerospace', 'metals', 'iot_telecom', 'utilities', 'pulp_paper', 'automotive', 'datacenter', 'facility_management',
    // Phase 4 — non-industrial + medical-grade verticals (2026-06-27)
    'aquaculture_lobster', 'aquaculture_salmon', 'aquaculture_shrimp',
    'biosensor_ide_wearable', 'biosensor_continuous_glucose', 'biosensor_oximeter_fleet',
    'wearable_etd'],

  subPages: [
    {
      id: 'list',
      path: '',
      name: 'Asset List',
      icon: 'List',
      description: 'View and filter all monitored assets',
      component: 'AssetList',
      requiredPermissions: ['assets:read'],
      position: 1,
      industryLabels: {
        healthcare: 'Device List',
        financial: 'System List',
        transportation: 'Vehicle List',
        iot_telecom: 'SIM Fleet',
      },
    },
    {
      id: 'overview',
      path: ':assetId',
      name: 'Asset Overview',
      icon: 'Eye',
      description: 'Comprehensive view of asset status and metrics',
      component: 'AssetOverview',
      requiredPermissions: ['assets:read'],
      position: 2,
      requiresAssetId: true,
      industryLabels: {
        healthcare: 'Device Overview',
        financial: 'System Overview',
        transportation: 'Vehicle Overview',
        iot_telecom: 'SIM Detail',
      },
    },
    {
      id: 'health',
      path: ':assetId/health',
      name: 'Health Analysis',
      icon: 'HeartPulse',
      description: 'Detailed health score breakdown and trends',
      component: 'AssetHealthAnalysis',
      requiredPermissions: ['assets:read'],
      position: 3,
      requiresAssetId: true,
      industryLabels: {
        healthcare: 'Device Health Analysis',
        financial: 'System Health Analysis',
        iot_telecom: 'Signal Analysis',
      },
    },
    {
      id: 'anomaly',
      path: ':assetId/anomaly',
      name: 'Anomaly Fingerprint',
      icon: 'Fingerprint',
      description: 'Visual representation of anomaly patterns',
      component: 'AnomalyFingerprint',
      requiredPermissions: ['analytics:read'],
      position: 4,
      requiresAssetId: true,
      industryLabels: {
        healthcare: 'Deviation Fingerprint',
        financial: 'Outlier Analysis',
        iot_telecom: 'Network Anomaly Pattern',
      },
    },
    {
      id: 'timeline',
      path: ':assetId/timeline',
      name: 'Time-to-Failure',
      icon: 'Clock',
      description: 'Predicted failure timeline and risk factors',
      component: 'TimeToFailureTimeline',
      requiredPermissions: ['predictions:read'],
      position: 5,
      requiresAssetId: true,
      industryLabels: {
        healthcare: 'Service Prediction',
        financial: 'Risk Timeline',
        transportation: 'Breakdown Prediction',
        iot_telecom: 'Connectivity Forecast',
      },
    },
    {
      id: 'sensors',
      path: ':assetId/sensors',
      name: 'Sensor Deep Dive',
      icon: 'Radio',
      description: 'Detailed sensor data and correlation analysis',
      component: 'SensorAnalysis',
      requiredPermissions: ['analytics:read'],
      position: 6,
      requiresAssetId: true,
      industryLabels: {
        healthcare: 'Parameter Analysis',
        financial: 'Metrics Analysis',
        transportation: 'Telematics Analysis',
      },
    },
    {
      id: 'history',
      path: ':assetId/history',
      name: 'Historical Patterns',
      icon: 'History',
      description: 'Past events, maintenance history, and pattern matching',
      component: 'HistoricalPatterns',
      requiredPermissions: ['assets:read'],
      position: 7,
      requiresAssetId: true,
    },
    {
      id: 'model-report',
      path: ':assetId/model',
      name: 'Model Report',
      icon: 'FileText',
      description: 'AI model performance and validation metrics',
      component: 'ModelReport',
      requiredPermissions: ['models:read'],
      position: 8,
      requiresAssetId: true,
    },
  ],

  workflows: [
    {
      id: 'asset-investigation',
      name: 'Asset Investigation',
      description: 'Systematic investigation of asset health concerns',
      icon: 'Search',
      steps: [
        {
          id: 'review-health',
          name: 'Review Health Score',
          description: 'Check current health score and recent changes',
          navigateTo: 'health',
          icon: 'HeartPulse',
          estimatedDuration: 3,
        },
        {
          id: 'check-anomalies',
          name: 'Check Recent Anomalies',
          description: 'Review anomaly fingerprint for unusual patterns',
          navigateTo: 'anomaly',
          icon: 'Fingerprint',
          estimatedDuration: 5,
        },
        {
          id: 'analyze-sensors',
          name: 'Analyze Sensor Trends',
          description: 'Deep dive into sensor data for root causes',
          navigateTo: 'sensors',
          icon: 'Radio',
          estimatedDuration: 10,
        },
        {
          id: 'compare-history',
          name: 'Compare to History',
          description: 'Match current patterns against historical events',
          navigateTo: 'history',
          icon: 'History',
          estimatedDuration: 5,
        },
        {
          id: 'assess-timeline',
          name: 'Assess Failure Timeline',
          description: 'Review time-to-failure prediction',
          navigateTo: 'timeline',
          icon: 'Clock',
          estimatedDuration: 3,
        },
        {
          id: 'generate-report',
          name: 'Generate Report',
          description: 'Create investigation summary and recommendations',
          icon: 'FileText',
          estimatedDuration: 5,
        },
      ],
      triggerConditions: ['healthScoreDropped', 'anomalyDetected'],
    },
    {
      id: 'baseline-comparison',
      name: 'Baseline Comparison',
      description: 'Compare asset performance against baseline metrics',
      icon: 'GitCompare',
      steps: [
        {
          id: 'select-baseline',
          name: 'Select Baseline Period',
          description: 'Choose a historical period as baseline',
          icon: 'Calendar',
          estimatedDuration: 2,
        },
        {
          id: 'compare-metrics',
          name: 'Compare Key Metrics',
          description: 'Analyze deviations from baseline',
          icon: 'BarChart',
          estimatedDuration: 5,
        },
        {
          id: 'identify-changes',
          name: 'Identify Changes',
          description: 'Document what changed between periods',
          icon: 'FileSearch',
          estimatedDuration: 5,
        },
        {
          id: 'recommend-actions',
          name: 'Recommend Actions',
          description: 'Suggest corrective actions based on findings',
          icon: 'Lightbulb',
          estimatedDuration: 5,
        },
      ],
    },
  ],

  narratives: {
    sections: [
      {
        id: 'situation',
        name: 'Asset Status',
        template: '{assetName} ({assetType}) is currently at {healthScore}% {healthTerm}. Status: {statusLabel}. Last update: {lastUpdate}.',
        order: 1,
        required: true,
      },
      {
        id: 'anomaly-analysis',
        name: 'Anomaly Analysis',
        template: '{anomalyCount} anomalies detected in the last {timePeriod}. Primary deviation in {topDeviationSensor} ({deviationPercent}% above normal). Pattern suggests: {patternInterpretation}.',
        order: 2,
        condition: 'hasAnomalies',
      },
      {
        id: 'prediction',
        name: 'Failure Prediction',
        template: 'Time-to-{failureTerm}: {timeToFailure} ({confidence}% confidence). Primary risk factors: {riskFactors}.',
        order: 3,
        condition: 'hasPrediction',
      },
      {
        id: 'recommendation',
        name: 'Recommendation',
        template: '**Recommended Action:** {recommendedAction}\n**Priority:** {priority}\n**Estimated Impact:** {impactDescription}',
        order: 4,
        required: true,
      },
    ],
    availableVariables: [
      'assetName', 'assetType', 'healthScore', 'healthTerm', 'statusLabel', 'lastUpdate',
      'anomalyCount', 'timePeriod', 'topDeviationSensor', 'deviationPercent', 'patternInterpretation',
      'failureTerm', 'timeToFailure', 'confidence', 'riskFactors',
      'recommendedAction', 'priority', 'impactDescription',
    ],
  },

  industryOverrides: {
    healthcare: {
      name: 'Device Intelligence',
      description: 'Medical device analysis, calibration status, and service predictions',
      additionalSubPages: [
        {
          id: 'calibration',
          path: ':assetId/calibration',
          name: 'Calibration Status',
          icon: 'Settings',
          description: 'Calibration history and compliance status',
          component: 'CalibrationStatus',
          position: 9,
          requiresAssetId: true,
        },
      ],
    },
    financial: {
      name: 'System Intelligence',
      description: 'IT system analysis, performance metrics, and risk assessment',
    },
    transportation: {
      name: 'Vehicle Intelligence',
      description: 'Vehicle analysis, telematics, and service predictions',
      additionalSubPages: [
        {
          id: 'driver',
          path: ':assetId/driver',
          name: 'Driver Performance',
          icon: 'User',
          description: 'Driver behavior and performance metrics',
          component: 'DriverPerformance',
          position: 9,
          requiresAssetId: true,
        },
      ],
    },
    iot_telecom: {
      name: 'SIM Intelligence',
      description: 'VIoT-Flex Multi-IMSI SIM analysis with best network selection and eUICC management',
      icon: 'Radio',
      additionalSubPages: [
        {
          id: 'signal-analysis',
          path: ':assetId/signal',
          name: 'Signal Analysis',
          icon: 'Signal',
          description: 'Deep dive into RSSI, RSRP, RSRQ, and SINR metrics across all available networks',
          component: 'SignalAnalysis',
          position: 9,
          requiresAssetId: true,
        },
        {
          id: 'carrier-comparison',
          path: ':assetId/carriers',
          name: 'Carrier Comparison',
          icon: 'GitCompare',
          description: 'Compare signal quality and performance across Multi-IMSI carrier profiles',
          component: 'CarrierComparison',
          position: 10,
          requiresAssetId: true,
        },
        {
          id: 'network-profiles',
          path: ':assetId/profiles',
          name: 'Network Profiles',
          icon: 'Layers',
          description: 'eUICC profile management and Multi-IMSI configuration',
          component: 'NetworkProfiles',
          position: 11,
          requiresAssetId: true,
        },
        {
          id: 'best-network',
          path: ':assetId/best-network',
          name: 'Best Network Selection',
          icon: 'Award',
          description: 'AI-powered optimal carrier selection based on location, signal, and cost',
          component: 'BestNetworkSelection',
          position: 12,
          requiresAssetId: true,
        },
        {
          id: 'coverage-map',
          path: ':assetId/coverage',
          name: 'Coverage Analysis',
          icon: 'Map',
          description: 'Geographic coverage analysis with Multi-IMSI roaming capabilities',
          component: 'CoverageAnalysis',
          position: 13,
          requiresAssetId: true,
        },
        {
          id: 'data-usage',
          path: ':assetId/usage',
          name: 'Data Usage Analytics',
          icon: 'Activity',
          description: 'Data consumption patterns and cost optimization insights',
          component: 'DataUsageAnalytics',
          position: 14,
          requiresAssetId: true,
        },
      ],
      additionalWorkflows: [
        {
          id: 'sim-health-investigation',
          name: 'SIM Health Investigation',
          description: 'Systematic investigation of SIM connectivity issues',
          icon: 'Search',
          steps: [
            {
              id: 'check-signal',
              name: 'Check Signal Metrics',
              description: 'Review RSSI, RSRP, RSRQ, and SINR values',
              navigateTo: 'signal-analysis',
              icon: 'Signal',
              estimatedDuration: 3,
            },
            {
              id: 'compare-carriers',
              name: 'Compare Available Carriers',
              description: 'Analyze all Multi-IMSI profiles for best option',
              navigateTo: 'carrier-comparison',
              icon: 'GitCompare',
              estimatedDuration: 5,
            },
            {
              id: 'check-profiles',
              name: 'Verify eUICC Profiles',
              description: 'Ensure network profiles are correctly configured',
              navigateTo: 'network-profiles',
              icon: 'Layers',
              estimatedDuration: 3,
            },
            {
              id: 'review-coverage',
              name: 'Review Coverage',
              description: 'Check geographic coverage for the SIM location',
              navigateTo: 'coverage-map',
              icon: 'Map',
              estimatedDuration: 5,
            },
            {
              id: 'analyze-usage',
              name: 'Analyze Data Usage',
              description: 'Review data consumption patterns for anomalies',
              navigateTo: 'data-usage',
              icon: 'Activity',
              estimatedDuration: 3,
            },
          ],
          triggerConditions: ['signalDrop', 'connectivityLoss', 'highLatency'],
        },
        {
          id: 'optimal-carrier-selection',
          name: 'Optimal Carrier Selection',
          description: 'Select the best network for this SIM using Multi-IMSI technology',
          icon: 'Award',
          steps: [
            {
              id: 'benchmark-carriers',
              name: 'Benchmark All Carriers',
              description: 'Run signal quality tests across all available carriers',
              navigateTo: 'carrier-comparison',
              icon: 'BarChart2',
              estimatedDuration: 5,
            },
            {
              id: 'analyze-coverage',
              name: 'Analyze Geographic Coverage',
              description: 'Check coverage strength at SIM location',
              navigateTo: 'coverage-map',
              icon: 'Map',
              estimatedDuration: 3,
            },
            {
              id: 'cost-analysis',
              name: 'Analyze Cost Efficiency',
              description: 'Compare data rates across carriers',
              navigateTo: 'data-usage',
              icon: 'DollarSign',
              estimatedDuration: 3,
            },
            {
              id: 'select-best',
              name: 'Select Best Network',
              description: 'AI-recommended optimal carrier selection',
              navigateTo: 'best-network',
              icon: 'Award',
              estimatedDuration: 2,
            },
            {
              id: 'switch-profile',
              name: 'Switch Profile',
              description: 'Execute eUICC profile switch if beneficial',
              icon: 'RefreshCw',
              estimatedDuration: 2,
            },
          ],
          triggerConditions: ['newSimDeployment', 'signalDegradation', 'costOptimization'],
        },
      ],
    },
    datacenter: {
      name: 'Server Intelligence',
      description: 'Server rack analysis, thermal monitoring, and power consumption predictions',
      icon: 'Server',
    },
  },

  requiredPermissions: ['assets:read'],
}
